// Statistics events (WS-49 T5, contract section 13): counts only, no visitor identifier, no cookie, no storage.
// The engine calls track(event, data) and nothing else. The sink is empty (nothing is sent) until main.js plugs the
// beacon in, which it does only when the boot record names a beacon address (WS-50). A broken sink can never break
// the map.
// WS-73: the gate. Nothing leaves until the website around the map says yes to statistics (its cookie banner, through
// web/js/lazy/consent.js, which calls setAllowed). The gate starts closed; a no or a withdrawn yes closes it again at
// once. Only click events are sent: the plain view is counted by the map Worker from the page request
// (worker/views.js), so `map_view` never leaves the browser. Sentry error reports are not gated (web/js/errors.js).
/** @typedef {import('./settings/typedefs.js').TrackEvent} TrackEvent */

const EVENTS = new Set(['place_open', 'booking_click', 'tour_start', 'tour_end', 'interior_open', 'gallery_open']);
const EXTRA = ['target', 'provider', 'mode', 'tour']; // the only fields an event may carry besides the ids
const ID = /^[a-z0-9_-]{1,64}$/;
let sink = null;
let allowed = false;

/**
 * Open or close the gate: only `true` opens it (the website's yes); anything else closes it.
 * @param {boolean} on
 */
export function setAllowed(on) {
  allowed = on === true;
}

// The boot record: what the host pins on the page, the way worker/index.js pins `mapmystay-resort` today.
// A page without it (local dev, today's hosts) sends events without those ids.
function bootIds() {
  const meta = (n) => globalThis.document?.querySelector(`meta[name="mapmystay-${n}"]`)?.content || '';
  const ids = { map: meta('map') || meta('resort'), preset: meta('preset'), placement: meta('placement') };
  return Object.fromEntries(Object.entries(ids).filter(([, v]) => ID.test(v)));
}

/**
 * Where the events go. Pass a function `(record) => void` to receive `{ event, target?, provider?, mode?, tour?,
 * map?, preset?, placement? }`; anything else unplugs it again.
 * @param {((record: object) => unknown) | null} fn
 */
export function setSink(fn) {
  sink = typeof fn === 'function' ? fn : null;
}

/**
 * The sink of a published map (WS-50): each event leaves as one `navigator.sendBeacon` to the address the boot record
 * names, the record as JSON text (worker/store.js `BeaconRecord`). Queued by the browser: it never waits and never
 * answers. An event without the map and preset ids cannot be counted and is not sent.
 * @param {string} address
 * @param {(address: string, body: string) => unknown} [send]
 * @returns {(record: object) => void}
 */
export function beaconSink(address, send = (a, body) => globalThis.navigator?.sendBeacon?.(a, body)) {
  return (record) => { if (record.map && record.preset) send(address, JSON.stringify(record)); };
}

/**
 * Count one event. Unknown event names and unknown fields are dropped. Never throws and never waits.
 * @param {TrackEvent} event
 * @param {{ target?: string, provider?: string, mode?: string, tour?: string }} [data]
 * @returns {void}
 */
export function track(event, data) {
  try {
    if (!sink || !allowed || !EVENTS.has(event)) return;
    const record = { event };
    for (const k of EXTRA) if (typeof data?.[k] === 'string' && data[k]) record[k] = data[k].slice(0, 80);
    const sent = sink(Object.assign(record, bootIds()));
    sent?.catch?.(() => {}); // a sink that returns a promise must not leave a rejection behind
  } catch { /* errors:ignore: statistics must never break the map */ }
}
